import { ChangeEvent } from 'react';

import { Dayjs } from 'dayjs';
import { ProjectResponse, WorkPlaceResponse } from 'generated/index';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Event } from 'components/roster-portal/activity-planning/types';

import { GRAY } from 'constants/colors';
import { WEEKDAYS } from 'constants/date';

import { shouldDisableDateInRange } from 'utils/date';

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  StepLabel
} from '@mui/material';

import {
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledSelect,
  ControlledTextField,
  ControlledTimePicker
} from 'components/shared/ControlledFields';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { FieldKey } from '../input-field-grants';

type Props = {
  enable?: boolean;
  control: Control<any>;
  event?: Event;
  minuteDuration?: number;
  hourDuration?: number;
  periodType?: 'WEEK' | 'DAY' | undefined;
  dayInWeekSet: string[];
  selectedProject?: ProjectResponse | null;
  selectedWorkPlace?: WorkPlaceResponse | null;
  hasFieldGrant: (key: FieldKey) => boolean;
  setValue: UseFormSetValue<any>;
};

const WhenSection = ({
  enable,
  control,
  event,
  minuteDuration,
  hourDuration,
  periodType,
  dayInWeekSet,
  selectedProject,
  selectedWorkPlace,
  hasFieldGrant,
  setValue
}: Props) => {
  const { t } = useTranslation();

  if (!enable) return null;

  return (
    <MultiStepForm.Section
      title="Wanneer"
      validateFields={[
        'duration',
        'startDate',
        'startTime',
        ...(hasFieldGrant('START_DATE_VALIDATION') ? ['startDateIsValid'] : []),
        ...(hasFieldGrant('DAY_IN_WEEK_SET') ? ['dayInWeekSet'] : [])
      ]}
    >
      <Box display="flex" gap={2.5}>
        <ControlledDatePicker
          name="startDate"
          label="Startdatum"
          control={control}
          variant="outlined"
          fullWidth={hasFieldGrant('START_DATE_FULL_WIDTH')}
          shouldDisableDate={(date: Dayjs) => {
            const { validFrom, validTo } = selectedProject ||
              selectedWorkPlace || {
                validFrom: null,
                validTo: null
              };
            return shouldDisableDateInRange(
              date,
              new Date(validFrom || NaN),
              new Date(validTo || NaN)
            );
          }}
        />
        {hasFieldGrant('START_TIME') && (
          <ControlledTimePicker
            name="startTime"
            label="Begintijd"
            control={control}
            variant="outlined"
          />
        )}
      </Box>

      <Box display="flex" gap={2.5}>
        <ControlledTextField
          name="hourDuration"
          label={t('hours')}
          control={control}
          variant="outlined"
          sx={{ width: '60px' }}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*'
          }}
          onBlur={() => {
            if (!minuteDuration && hourDuration) setValue('minuteDuration', 0);
            if (!hourDuration) setValue('hourDuration', 0);
          }}
        />

        <ControlledTextField
          name="minuteDuration"
          label={t('minutes')}
          control={control}
          variant="outlined"
          sx={{ width: '60px' }}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*'
          }}
          onBlur={() => {
            if (minuteDuration && !hourDuration) setValue('hourDuration', 0);
            if (!minuteDuration) setValue('minuteDuration', 0);
          }}
        />

        {hasFieldGrant('END_TIME') && (
          <ControlledTimePicker
            name="endTime"
            control={control}
            label="Eindtijd"
            variant="outlined"
            disabled
          />
        )}
      </Box>

      <Box>
        {hasFieldGrant('RECURRENCE_RULE_EDIT') && (
          <Box sx={{ marginBottom: '10px' }}>
            <ControlledCheckbox
              control={control}
              name="isRecurring"
              label={t('recurrenceRule')}
            />
          </Box>
        )}

        {hasFieldGrant('PERIOD_TYPE') && (
          <Box display="flex" gap={2.5}>
            <ControlledSelect
              control={control}
              name="periodType"
              label={t('repeatOn')}
              variant="outlined"
              options={[
                { label: t('timeUnits.DAY'), value: 'DAY' },
                { label: t('timeUnits.WEEK'), value: 'WEEK' }
              ]}
            />

            <ControlledTextField
              name="interval"
              label={t('interval')}
              control={control}
              variant="outlined"
              sx={{ width: '144px' }}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    disableTypography
                    sx={{ color: GRAY, fontSize: '12px' }}
                  >
                    {periodType === 'DAY' ? 'dagen' : 'weken'}
                  </InputAdornment>
                )
              }}
            />
          </Box>
        )}

        {hasFieldGrant('WEEK_DAYS') && (
          <Box>
            <FormLabel sx={{ marginTop: '24px' }}>{t('repeatOn')}</FormLabel>
            <Grid container flexDirection="column">
              {WEEKDAYS.map((d: string, i: number) => (
                <Grid item xs={2} key={i}>
                  <Controller
                    key={d}
                    name="dayInWeekSet"
                    control={control}
                    render={({ field }) => {
                      const value: string = d.toUpperCase();
                      return (
                        <FormControlLabel
                          {...field}
                          label={t(`weekday.${d}`)}
                          control={
                            <Checkbox
                              value={value}
                              disabled={periodType !== 'WEEK'}
                              checked={(dayInWeekSet || [])?.includes(value)}
                              onChange={(
                                event: ChangeEvent<HTMLInputElement>,
                                checked: boolean
                              ) => {
                                const v: string =
                                  event.target.value.toUpperCase();
                                field.onChange(
                                  checked
                                    ? [...field.value, v]
                                    : field.value.filter(
                                        (value: string) => value !== v
                                      )
                                );
                              }}
                            />
                          }
                        />
                      );
                    }}
                  />
                </Grid>
              ))}

              <Grid item xs={4}>
                <Controller
                  name="dayInWeekSet"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      label={t('everyDay')}
                      control={
                        <Checkbox
                          {...field}
                          checked={dayInWeekSet.length > 0}
                          disabled={periodType !== 'WEEK'}
                          indeterminate={
                            dayInWeekSet.length > 0 &&
                            dayInWeekSet.length < WEEKDAYS.length
                          }
                          onChange={(_, checked) =>
                            setValue(
                              'dayInWeekSet',
                              checked
                                ? WEEKDAYS.map((d: string) => d.toUpperCase())
                                : []
                            )
                          }
                        />
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {hasFieldGrant('RECURRENCE_RULE_VIEW') && (
        <Box display="flex" gap={2.5}>
          <StepLabel>{t('recurrenceRule')}</StepLabel>
          <Box>{event?.recurrenceRule}</Box>
        </Box>
      )}
    </MultiStepForm.Section>
  );
};

export default WhenSection;
